/**
 * Диалоги управления комнатами: создать комнату, создать личный чат,
 * пригласить участника, выйти из комнаты, отклонить приглашение.
 *
 * ПОЧЕМУ ОДИН ФАЙЛ И ОДИН ТИП СОСТОЯНИЯ (`DialogRequest`): диалог всегда ровно
 * один, и держать пять независимых булевых флагов в App значит рано или поздно
 * открыть два окна сразу. Одно поле-объединение делает это невозможным
 * по типу, а не по дисциплине.
 *
 * ПОДТВЕРЖДЕНИЕ У РАЗРУШИТЕЛЬНЫХ ДЕЙСТВИЙ ОБЯЗАТЕЛЬНО. Выход из комнаты — это
 * потеря доступа к истории (в приватной комнате вернуться можно только по
 * новому приглашению), поэтому отдельное окно, а не пункт меню сразу в дело.
 */
import { useState } from "react";
import type { Messages } from "../i18n/messages";
type TKey = keyof Messages;
import { tf, useT, useTF } from "../i18n";
import type { MatrixClient, Room } from "matrix-js-sdk";

import {
  createDirectMessage,
  createRoom,
  createSpace,
  declineInvite,
  humanError,
  inviteUser,
  mayManageSpaceChild,
  moveRoomToSpace,
  whyUserUnusable,
  leaveRoom,
  whyCannotLeaveSpace,
  mayInviteToSpace,
} from "../matrix/roomActions";
import { spaceMembership } from "../matrix/rooms";
import { spaceUserSuggestions, whyDmNotAllowed, isOutsideSpace } from "../matrix/spaceUsers";
import { Dialog, DialogButton, Field, useAsyncAction } from "./Dialog";
import { RoomLinkDialog } from "./RoomLink";
import { RoomSettings } from "./RoomSettings";
import { SpaceMembers } from "./SpaceMembers";
import { UserSuggest, filterSuggestions } from "./UserSuggest";
import { s } from "./styles";
import { color, font, radius, space } from "./theme";

export type DialogRequest =
  | { kind: "create" }
  | { kind: "createSpace" }
  | { kind: "dm" }
  | { kind: "invite"; roomId: string; roomName: string }
  /** «Ссылка на комнату / Пригласить по ссылке» (Антон 16.10 10:33). */
  | { kind: "roomLink"; roomId: string; roomName: string }
  /**
   * "Add participant" from a DM call (Anton 2026-09-17 08:19): a DM stays a
   * DM with its own history. Picking a third person creates a NEW private
   * group room with the DM partner and the new person (auto-joined) and
   * hands its id to `onCreated`, which moves the call there.
   */
  | { kind: "callAddFromDm"; roomId: string; roomName: string; partnerId: string; onCreated: (roomId: string) => void }
  | { kind: "inviteSpace"; spaceId: string; spaceName: string }
  | { kind: "moveRoom"; roomId: string; roomName: string }
  | { kind: "editSpace"; spaceId: string; spaceName: string }
  | { kind: "spaceMembers"; spaceId: string; spaceName: string }
  | { kind: "leave"; roomId: string; roomName: string }
  | { kind: "decline"; roomId: string; roomName: string };

export function RoomDialogs({
  client,
  request,
  onClose,
  onRoomCreated,
  onRoomLeft,
  activeSpaceId,
}: {
  client: MatrixClient;
  request: DialogRequest | null;
  /** Пространство, выбранное в рельсе (null = «Дом» → показываем всех). */
  activeSpaceId?: string | null;
  onClose: () => void;
  /** Созданную комнату сразу открываем — иначе непонятно, создалась ли она. */
  onRoomCreated: (roomId: string) => void;
  /** Из покинутой комнаты надо уйти в «выберите чат», иначе останется пустая лента. */
  onRoomLeft: (roomId: string) => void;
}) {
  if (!request) return null;
  switch (request.kind) {
    case "create":
      return (
        <CreateRoomDialog
          client={client}
          onClose={onClose}
          onCreated={onRoomCreated}
          activeSpaceId={activeSpaceId}
        />
      );
    case "createSpace":
      return (
        <CreateSpaceDialog client={client} onClose={onClose} onCreated={onRoomCreated} />
      );
    case "dm":
      return (
        <CreateDmDialog
          client={client}
          onClose={onClose}
          onCreated={onRoomCreated}
          activeSpaceId={activeSpaceId}
        />
      );
    case "invite":
      return (
        <InviteDialog
          client={client}
          roomId={request.roomId}
          roomName={request.roomName}
          onClose={onClose}
        />
      );
    case "roomLink":
      return (
        <RoomLinkDialog
          client={client}
          roomId={request.roomId}
          roomName={request.roomName}
          onClose={onClose}
        />
      );
    case "callAddFromDm":
      return (
        <InviteDialog
          client={client}
          roomId={request.roomId}
          roomName={request.roomName}
          onClose={onClose}
          groupFromDm={{ partnerId: request.partnerId, onCreated: request.onCreated }}
        />
      );
    case "inviteSpace":
      return (
        <InviteSpaceDialog
          client={client}
          spaceId={request.spaceId}
          spaceName={request.spaceName}
          onClose={onClose}
        />
      );
    case "moveRoom":
      return (
        <MoveRoomDialog
          client={client}
          roomId={request.roomId}
          roomName={request.roomName}
          onClose={onClose}
        />
      );
    case "spaceMembers":
      return <SpaceMembers client={client} spaceId={request.spaceId} onClose={onClose} />;
    case "editSpace":
      return (
        <EditSpaceDialog
          client={client}
          spaceId={request.spaceId}
          onClose={onClose}
          onLeft={onRoomLeft}
        />
      );
    case "leave":
      return (
        <LeaveDialog
          client={client}
          roomId={request.roomId}
          roomName={request.roomName}
          onClose={onClose}
          onLeft={onRoomLeft}
        />
      );
    case "decline":
      return (
        <DeclineDialog
          client={client}
          roomId={request.roomId}
          roomName={request.roomName}
          onClose={onClose}
          onLeft={onRoomLeft}
        />
      );
  }
}

function CreateRoomDialog({
  client,
  onClose,
  onCreated,
  activeSpaceId,
}: {
  client: MatrixClient;
  onClose: () => void;
  onCreated: (roomId: string) => void;
  /** Активное пространство из рельсы: сюда и положим новую комнату. */
  activeSpaceId?: string | null;
}) {
  const t = useT();
  const tfmt = useTF();
  const [name, setName] = useState("");
  const [isPublic, setPublic] = useState(false);
  const spaceName = activeSpaceId
    ? (client.getRoom(activeSpaceId)?.name ?? t("dialog.selectedSpace"))
    : null;
  const { busy, error, run } = useAsyncAction(onClose);

  const submit = () =>
    void run(async () => {
      try {
        // ПРИВЯЗЫВАЕМ К АКТИВНОМУ ПРОСТРАНСТВУ (Антон 12.09 10:57:
        // «комнату novaja я создавал, находясь в пространстве ALG» — а она
        // оказалась вне пространств). «Дом» = вне пространств, как и было.
        const id = await createRoom(client, {
          name: name.trim(),
          isPublic,
          parentSpaceId: activeSpaceId ?? null,
        });
        onCreated(id);
      } catch (e) {
        throw new Error(humanError(e));
      }
    });

  return (
    <Dialog
      title={t("dialog.newRoomTitle")}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy || !name.trim()}
            onClick={submit}
            testId="dialog-submit"
          >
            {busy ? t("dialog.createProgress") : t("common.create")}
          </DialogButton>
        </>
      }
    >
      <Field
        label={t("dialog.newRoomNameLabel")}
        value={name}
        onChange={setName}
        placeholder={t("dialog.newRoomNamePlaceholder")}
        testId="field-room-name"
        onEnter={() => name.trim() && submit()}
      />
      {/* ГДЕ СОЗДАЁТСЯ КОМНАТА — ГОВОРИМ ЯВНО. Антон 12.09 10:57 создавал
          novaja «находясь в пространстве ALG», а она попала вне пространств:
          интерфейс не показывал, куда именно кладётся комната, и разойтись
          с ожиданием мог незаметно. Теперь место назначения написано. */}
      <div
        style={{ color: color.textDim, fontSize: font.micro }}
        data-testid="create-room-target"
      >
        {spaceName
          ? tfmt("dialog.roomCreatedInSpace", { space: spaceName })
          : t("ui.komnata_budet_sozdana_vne_prostranstv_do")}
      </div>
      <label style={{ display: "flex", gap: space.md, alignItems: "flex-start", fontSize: font.small }}>
        <input
          type="checkbox"
          checked={isPublic}
          data-testid="field-room-public"
          onChange={(e) => setPublic(e.target.checked)}
        />
        <span>{t("dialog.publicRoomLabel")}<div style={{ color: color.textDim, fontSize: font.micro, marginTop: 2 }}>
            {/* Честно пишем, что именно делает галочка: «публичная» без публикации
                в каталоге выглядела бы как неработающая настройка. */}
            {t("dialog.newRoomDescription")}</div>
        </span>
      </label>
    </Dialog>
  );
}

/**
 * Разбор Matrix ID с ВНЯТНОЙ причиной отказа.
 *
 * Прежняя проверка `/^@[^:\s]+:[^:\s]+$/` пропускала `@ьфьф:algsussecc.c`
 * (Антон, 2026-09-10 11:13): русская раскладка плюс опечатка в домене.
 * Кнопка была активна, приглашение уходило на несуществующего пользователя
 * и падало уже на сервере. Ошибку надо ловить ДО отправки и говорить, что
 * именно не так — «неверный формат» здесь бесполезно.
 *
 * Грамматика localpart по спецификации Matrix: a-z, 0-9, точка, дефис,
 * подчёркивание, знак равенства, плюс, слэш. Заглавные латинские историю
 * знают, но сервер их приводит к нижнему регистру — не ругаемся.
 */
export function mxidProblem(raw: string, t: (k: TKey) => string): string | null {
  const id = raw.trim();
  if (id === "" || id === "@") return null; // ещё не начали вводить — молчим
  if (!id.startsWith("@")) return t("dialog.matrixIdInvalidPrefix");
  if (/\s/.test(id)) return t("dialog.matrixIdInvalidSpaces");

  const colon = id.indexOf(":");
  if (colon < 0) return t("dialog.matrixIdMissingServer");

  const local = id.slice(1, colon);
  const server = id.slice(colon + 1);

  if (local === "") return t("ui.pustoe_imya_polzovatelya_do_dvoetochiya");
  // Кириллица — почти всегда незамеченная русская раскладка. Проверяем ПЕРВОЙ:
  // это самая частая ошибка и самая непонятная, если сказать просто «неверный формат».
  if (/[\u0400-\u04FF]/.test(local) || /[\u0400-\u04FF]/.test(server)) {
    return t("dialog.matrixIdCyrillic");
  }
  if (!/^[a-zA-Z0-9._=+/-]+$/.test(local)) {
    return t("dialog.matrixIdInvalidLocalpart");
  }
  if (server === "") return t("dialog.matrixIdMissingServerAddress");

  const host = server.split(":")[0];
  // Одиночная метка без точки (localhost) технически легальна, но в ALG
  // сервер всегда доменный — предупредим, это ловит `algsuccess` без зоны.
  if (!host.includes(".")) return t("dialog.matrixIdNoDomain");
  const tld = host.slice(host.lastIndexOf(".") + 1);
  if (tld.length < 2) return tf("dialog.domainLooksTruncated", { host });
  if (!/^[a-zA-Z0-9.-]+$/.test(host)) return t("dialog.matrixIdBadDomain");
  return null;
}

function CreateDmDialog({
  client,
  onClose,
  onCreated,
  activeSpaceId,
}: {
  client: MatrixClient;
  onClose: () => void;
  onCreated: (roomId: string) => void;
  activeSpaceId?: string | null;
}) {
  const t = useT();
  // Пустая строка, а НЕ "@": поле теперь и поиск по имени тоже (скриншот
  // Антона 11:04 — человек набирает «a» и ждёт список). Готовая «собака»
  // заставляла бы стирать её перед каждым поиском по имени.
  const [userId, setUserId] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const { busy, error, run } = useAsyncAction(onClose);
  const problem = mxidProblem(userId, t);
  const valid = userId.trim().length > 1 && problem === null;

  // Подсказки по людям из ВСЕХ пространств аккаунта (у DM нет целевой
  // комнаты, поэтому собираем шире). Исключается только сам себя.
  // Сужаем до АКТИВНОГО пространства (Антон 12.09 10:52): «Дом» —
  // все мои пространства, выбранное пространство — только его участники.
  const suggestions = spaceUserSuggestions(client, null, undefined, activeSpaceId);
  const filtered = showSuggest ? filterSuggestions(suggestions, userId) : [];
  // См. комментарий в InviteDialog: поиск по имени — не ошибка ввода.
  const searching = !userId.trim().startsWith("@") && filtered.length > 0;
  const shownProblem = searching ? null : problem;

  const submit = () =>
    void run(async () => {
      try {
        // ГРАНИЦА ПРОСТРАНСТВА ДЛЯ ЛИЧНОГО ЧАТА (Антон 12.09 10:47).
        // Как и в приглашении, проверяем при ОТПРАВКЕ: дропдаун можно
        // обойти, впечатав полный MXID руками.
        const outside = whyDmNotAllowed(client, userId.trim());
        if (outside) throw new Error(outside);
        const id = await createDirectMessage(client, userId.trim());
        onCreated(id);
      } catch (e) {
        throw new Error(humanError(e));
      }
    });

  return (
    <Dialog
      title={t("dialog.dmTitle")}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy || !valid}
            onClick={submit}
            testId="dialog-submit"
          >
            {busy ? t("dialog.createProgress") : t("common.create")}
          </DialogButton>
        </>
      }
    >
      <div
        style={{ position: "relative" }}
        onBlur={(e) => {
          // Закрываем дропдаун при потере фокуса, НО не когда фокус ушёл
          // на элемент самого дропдауна (relatedTarget внутри обёртки).
          // Без этого список висел бы поверх кнопок диалога и перехватывал клики.
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setShowSuggest(false);
          }
        }}
      >
        <Field
          label={t("dialog.dmIdLabel")}
          value={userId}
          onChange={(v) => {
            setUserId(v);
            setShowSuggest(true);
          }}
          placeholder="@ivanov:algsuccess.com"
          testId="field-dm-user"
          problem={shownProblem}
          onEnter={() => valid && submit()}
        />
        {filtered.length > 0 && (
          <UserSuggest
            suggestions={filtered}
            query={userId}
            onSelect={(id) => {
              setUserId(id);
              setShowSuggest(false);
            }}
            onDismiss={() => setShowSuggest(false)}
          />
        )}
      </div>
      <div style={{ color: color.textDim, fontSize: font.micro }}>
        {/* Подсказки работают только по людям из пространств аккаунта.
            Кого нет в пространстве — вводится полным идентификатором. */}
        {activeSpaceId
          ? t("ui.pokazany_uchastniki_vybrannogo_prostranst")
          : t("dialog.dmAllSpacesHint")}
      </div>
    </Dialog>
  );
}

function InviteDialog({
  client,
  roomId,
  roomName,
  onClose,
  groupFromDm,
}: {
  client: MatrixClient;
  roomId: string;
  roomName: string;
  onClose: () => void;
  /** Set when adding a third person from a DM call: create a group instead of inviting into the DM. */
  groupFromDm?: { partnerId: string; onCreated: (roomId: string) => void };
}) {
  const t = useT();
  const tfmt = useTF();
  // Пустая строка, а НЕ "@": поле теперь и поиск по имени тоже (скриншот
  // Антона 11:04 — человек набирает «a» и ждёт список). Готовая «собака»
  // заставляла бы стирать её перед каждым поиском по имени.
  const [userId, setUserId] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const { busy, error, run } = useAsyncAction(() => {});
  const problem = mxidProblem(userId, t);
  const valid = userId.trim().length > 1 && problem === null;

  // Подсказки по людям из пространства комнаты, МИНУС уже состоящие
  // в ней (join + invite). Решение Антона 12.09 08:53: «я бы не показывал
  // тех кто уже в комнате». Leave и ban НЕ исключаем.
  const suggestions = spaceUserSuggestions(client, roomId, roomId);
  const filtered = showSuggest ? filterSuggestions(suggestions, userId) : [];
  // ПОКА ЧЕЛОВЕК ИЩЕТ ПО ИМЕНИ — НЕ РУГАЕМСЯ (скриншот Антона 12.09 11:04:
  // набрано «a», под полем красное «Matrix ID начинается с @», а под ним
  // спокойно стоит список найденных людей). Поле совмещает два способа
  // ввода: поиск по имени и точный MXID. Пока строка не похожа на MXID
  // и что-то нашлось — это поиск, а не ошибка. Ошибку показываем, когда
  // ввод начат с @ (человек явно печатает идентификатор) либо когда
  // подсказок не осталось и строку больше нечем объяснить.
  const searching = !userId.trim().startsWith("@") && filtered.length > 0;
  const shownProblem = searching ? null : problem;

  // ПРЕДУПРЕЖДЕНИЕ О ВНЕШНЕМ АДРЕСАТЕ (Антон 05.10 14:17).
  // Человек введён полным корректным MXID, приглашение РАЗРЕШЕНО, но
  // Антон должен видеть, что адресат не из пространства комнаты.
  // Показываем баннер под полем, но НЕ блокируем кнопку.
  const showOutsideWarning =
    valid && !searching && isOutsideSpace(client, roomId, userId.trim());

  const submit = () =>
    void run(async () => {
      const id = userId.trim();
      // ГРАНИЦА ПРОСТРАНСТВА — ПРЕДУПРЕЖДЕНИЕ, А НЕ ОТКАЗ.
      // Антон 05.10 14:17: «Разрешить и на вэбе». Раньше здесь был
      // жёсткий запрет: whyOutsideSpace возвращал текст ошибки, и
      // приглашение блокировалось даже при корректном полном MXID.
      // Теперь — предупреждение в UI (баннер под полем, см. ниже),
      // а отправка идёт дальше. Подсказки по-прежнему только из
      // пространства, серверный каталог не зовём.
      const unusable = await whyUserUnusable(client, id);
      if (unusable) throw new Error(unusable);
      if (groupFromDm) {
        // New private group: me + DM partner + the new person. Name it after
        // the people; auto-join on the server makes them members at once.
        const nameOf = (uid: string) =>
          client.getRoom(roomId)?.getMember(uid)?.name ?? client.getUser(uid)?.displayName ?? uid.slice(1, uid.indexOf(":"));
        const me = client.getUserId() ?? "";
        const groupName = [me, groupFromDm.partnerId, id].map(nameOf).join(", ");
        let newRoomId: string;
        try {
          newRoomId = await createRoom(client, { name: groupName, isPublic: false });
          await inviteUser(client, newRoomId, groupFromDm.partnerId);
          await inviteUser(client, newRoomId, id);
        } catch (e) {
          throw new Error(humanError(e));
        }
        onClose();
        groupFromDm.onCreated(newRoomId);
        return;
      }
      try {
        await inviteUser(client, roomId, id);
      } catch (e) {
        throw new Error(humanError(e));
      }
      // Окно НЕ закрываем: чаще всего приглашают сразу нескольких, и
      // переоткрывать диалог на каждого — лишние четыре клика.
      setDone(id);
      setUserId("");
      setShowSuggest(false);
    });

  return (
    <Dialog
      title={tfmt("dialog.inviteTitle", { room: roomName })}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">{t("common.close")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy || !valid}
            onClick={submit}
            testId="dialog-submit"
          >
            {busy ? t("dialog.inviteProgress") : t("common.invite")}
          </DialogButton>
        </>
      }
    >
      <div
        style={{ position: "relative" }}
        onBlur={(e) => {
          // Закрываем дропдаун при потере фокуса (см. комментарий в DmDialog).
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setShowSuggest(false);
          }
        }}
      >
        <Field
          label="Matrix ID"
          value={userId}
          onChange={(v) => {
            setUserId(v);
            setShowSuggest(true);
          }}
          placeholder="@ivanov:algsuccess.com"
          testId="field-invite-user"
          problem={shownProblem}
          onEnter={() => valid && submit()}
        />
        {filtered.length > 0 && (
          <UserSuggest
            suggestions={filtered}
            query={userId}
            onSelect={(id) => {
              setUserId(id);
              setShowSuggest(false);
            }}
            onDismiss={() => setShowSuggest(false)}
          />
        )}
      </div>
      {/* ПУСТОЙ СПИСОК ОБЪЯСНЯЕМ СЛОВАМИ (Антон 12.09 10:40: «дропдауны
          не работают»). Молча пустой дропдаун неотличим от сломанного —
          именно так дефект и выглядел. Две причины пустоты, два текста:
          «предлагать некого» и «ничего не совпало с набранным». */}
      {showSuggest && filtered.length === 0 && (
        <div
          style={{ color: color.textDim, fontSize: font.micro }}
          data-testid="invite-suggest-empty"
        >
          {suggestions.length === 0
            ? t("dialog.inviteSuggestNone")
            : t("dialog.inviteSuggestEmpty")}
        </div>
      )}
      {/* ПРЕДУПРЕЖДЕНИЕ О ВНЕШНЕМ АДРЕСАТЕ (Антон 05.10 14:17).
          Приглашение разрешено, но человек должен видеть, что адресат
          не из пространства комнаты. */}
      {showOutsideWarning && (
        <div
          style={{
            padding: `${space.sm}px ${space.md}px`,
            borderRadius: radius.sm,
            background: color.warnBg,
            border: `1px solid ${color.warnBorder}`,
            color: color.warn,
            fontSize: font.small,
            lineHeight: 1.5,
          }}
          data-testid="invite-outside-warning"
        >
          {tfmt("dialog.inviteOutsideSpaceWarning", { user: userId.trim() })}
        </div>
      )}
      {done && (
        <div style={{ color: color.ok, fontSize: font.small }} data-testid="invite-done">
          {tfmt("dialog.inviteDone", { user: done })}
        </div>
      )}
    </Dialog>
  );
}

function LeaveDialog({
  client,
  roomId,
  roomName,
  onClose,
  onLeft,
}: {
  client: MatrixClient;
  roomId: string;
  roomName: string;
  onClose: () => void;
  onLeft: (roomId: string) => void;
}) {
  const t = useT();
  const { busy, error, run } = useAsyncAction(onClose);
  // ЗАПРЕТ ПОСЛЕДНЕМУ АДМИНУ (Антон 12.09 11:51). Считаем ЗДЕСЬ, а не
  // прячем пункт меню: человек должен узнать ПРИЧИНУ, а исчезнувший пункт
  // ничего не объясняет. Для обычных комнат всегда null — правило про
  // пространства.
  const blocked = client.getRoom(roomId)?.isSpaceRoom()
    ? whyCannotLeaveSpace(client, roomId)
    : null;
  const isSpace = Boolean(client.getRoom(roomId)?.isSpaceRoom());
  return (
    <Dialog
      title={isSpace ? t("dialog.roomLeaveSpaceTitle") : t("dialog.leaveTitle")}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">
            {blocked ? t("dialog.understood") : t("common.cancel")}
          </DialogButton>
          <DialogButton
            kind="danger"
            disabled={busy || Boolean(blocked)}
            testId="dialog-submit"
            onClick={() =>
              void run(async () => {
                try {
                  await leaveRoom(client, roomId);
                } catch (e) {
                  throw new Error(humanError(e));
                }
                onLeft(roomId);
              })
            }
          >
            {busy ? t("dialog.leaveProgress") : t("app.logout")}
          </DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.body, lineHeight: 1.45 }}>
        {blocked ? (
          <>{t("dialog.cannotLeaveSpace")}<b>«{roomName}»</b>. {blocked}
          </>
        ) : isSpace ? (
          <>{t("dialog.youWillLeaveSpace")}<b>«{roomName}»</b>{t("dialog.leaveSpaceTail")}</>
        ) : (
          <>{t("dialog.youWillLeaveRoom")}<b>«{roomName}»</b>{t("dialog.leaveRoomTail")}</>
        )}
      </div>
    </Dialog>
  );
}

function DeclineDialog({
  client,
  roomId,
  roomName,
  onClose,
  onLeft,
}: {
  client: MatrixClient;
  roomId: string;
  roomName: string;
  onClose: () => void;
  onLeft: (roomId: string) => void;
}) {
  const t = useT();
  const { busy, error, run } = useAsyncAction(onClose);
  return (
    <Dialog
      title={t("dialog.declineTitle")}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="danger"
            disabled={busy}
            testId="dialog-submit"
            onClick={() =>
              void run(async () => {
                try {
                  await declineInvite(client, roomId);
                } catch (e) {
                  throw new Error(humanError(e));
                }
                onLeft(roomId);
              })
            }
          >
            {busy ? t("dialog.declineProgress") : t("common.decline")}
          </DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.body, lineHeight: 1.45 }}>{t("dialog.invitationTo")}<b>«{roomName}»</b>{t("dialog.inviteDeclineTail")}</div>
    </Dialog>
  );
}

// --- Управление ПРОСТРАНСТВАМИ (задача 12.09, блокер приёмки спейсов) ------

/**
 * Создать пространство. До этого диалога спейс из клиента создать было
 * НЕВОЗМОЖНО вообще: рельс только показывал существующие. Тема опциональна —
 * заставлять её заполнять значило бы получать «тема: тема» в каждом спейсе.
 */
function CreateSpaceDialog({
  client,
  onClose,
  onCreated,
}: {
  client: MatrixClient;
  onClose: () => void;
  onCreated: (roomId: string) => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  // ДОБАВЛЕНИЕ ЛЮДЕЙ ПРЯМО ПРИ СОЗДАНИИ (Антон 12.09 11:55). Раньше нужно
  // было создать спейс, найти его иконку, вызвать меню и звать по одному —
  // четыре шага вместо одного.
  const [picked, setPicked] = useState<string[]>([]);
  const [userId, setUserId] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  // Новое пространство ещё не существует, поэтому подсказки берём по
  // ВСЕМ моим пространствам (roomId = null): это ровно тот круг людей,
  // кого я и так вижу.
  const suggestions = spaceUserSuggestions(client, null);
  const filtered = showSuggest
    ? filterSuggestions(suggestions, userId).filter((u) => !picked.includes(u.userId))
    : [];
  const { busy, error, run } = useAsyncAction(onClose);

  const add = (id: string) => {
    const v = id.trim();
    if (!v || picked.includes(v)) return;
    setPicked([...picked, v]);
    setUserId("");
    setShowSuggest(false);
  };

  const submit = () =>
    void run(async () => {
      try {
        const { roomId, failed } = await createSpace(client, {
          name: name.trim(),
          topic: topic.trim(),
          invite: picked,
        });
        onCreated(roomId);
        if (failed.length > 0) {
          // Пространство СОЗДАНО — молчать про неудачные приглашения
          // нельзя, но и откатывать создание бессмысленно. Называем
          // поимённо, чтобы было понятно, кого позвать вручную.
          throw new Error(
            tfmt("dialog.spaceCreatedInviteFailed", {
              users: failed.map((f) => `${f.userId} (${f.reason})`).join("; "),
            }),
          );
        }
      } catch (e) {
        throw new Error(humanError(e));
      }
    });

  return (
    <Dialog
      title={t("dialog.newSpaceTitle")}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy || !name.trim()}
            onClick={submit}
            testId="dialog-submit"
          >
            {busy ? t("dialog.createProgress") : t("common.create")}
          </DialogButton>
        </>
      }
    >
      <Field
        label={t("dialog.newRoomNameLabel")}
        value={name}
        onChange={setName}
        placeholder={t("ui.naprimer_otdel_prodazh")}
        testId="field-space-name"
        onEnter={() => name.trim() && submit()}
      />
      <Field
        label={t("dialog.spaceTopicLabel")}
        value={topic}
        onChange={setTopic}
        placeholder={t("dialog.spaceTopicPlaceholder")}
        testId="field-space-topic"
      />
      <div style={{ position: "relative" }}>
        <Field
          label={t("dialog.spaceMembersLabel")}
          value={userId}
          onChange={(v) => {
            setUserId(v);
            setShowSuggest(true);
          }}
          placeholder={t("dialog.spaceMemberPlaceholder")}
          testId="field-space-invite"
          onEnter={() => add(userId)}
        />
        {filtered.length > 0 && (
          <UserSuggest
            suggestions={filtered}
            query={userId}
            onSelect={add}
            onDismiss={() => setShowSuggest(false)}
          />
        )}
      </div>
      {picked.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {picked.map((id) => (
            <span
              key={id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: font.micro,
                background: color.sidebarBg,
                border: `1px solid ${color.border}`,
                borderRadius: 999,
                padding: "3px 8px",
              }}
            >
              {id}
              <button
                type="button"
                aria-label={tfmt("dialog.removeEntry", { id })}
                onClick={() => setPicked(picked.filter((p) => p !== id))}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: color.textDim,
                  font: "inherit",
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ color: color.textDim, fontSize: font.micro }}>
        {/* Честно говорим, ЧТО даёт членство в спейсе (модель доступа п.4),
            чтобы не было иллюзии «добавил человека — он всё видит». */}
        {t("dialog.spaceDescription")}</div>
    </Dialog>
  );
}

/**
 * Перенести/привязать комнату к пространству. Чинит и старые комнаты вне
 * спейсов (`novaja` из дефекта 12.09 10:57) без пересоздания.
 *
 * СПИСОК ЦЕЛЕЙ. Показываем только те пространства, где у меня есть право
 * отправки m.space.child (mayManageSpaceChild): предложить человеку цель,
 * в которую сервер его всё равно не пустит, — это ложный интерфейс
 * (модель доступа п.3/4d: клиент не предлагает недопустимое, реальную
 * границу ставит сервер).
 *
 * ПУСТОЙ СПИСОК — НЕ МОЛЧИМ (требование постановки: пустой дропдаун
 * неотличим от поломки). Если целей нет, пишем словами, почему.
 */
function MoveRoomDialog({
  client,
  roomId,
  roomName,
  onClose,
}: {
  client: MatrixClient;
  roomId: string;
  roomName: string;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  // Текущие пространства комнаты — чтобы показать «откуда» и не предлагать
  // перенос «в самого себя». Берём из spaceMembership — того же источника,
  // что рельс и вкладки: иначе диалог и список видели бы разные картины.
  const memberOf = spaceMembership(client);
  const currentSpaces = [...(memberOf.get(roomId) ?? [])];
  const currentSet = new Set(currentSpaces);

  // Все мои пространства минус те, где комната уже состоит, минус те,
  // где у меня нет прав на m.space.child.
  const targets = client
    .getRooms()
    .filter((r) => r.isSpaceRoom() && r.getMyMembership() === "join")
    .filter((r) => !currentSet.has(r.roomId))
    .filter((r) => mayManageSpaceChild(client, r.roomId))
    .map((r) => ({ id: r.roomId, name: r.name || r.roomId }))
    .sort((a, b) => a.name.localeCompare(b.name, "ru"));

  // «Вне пространств (Дом)» доступно только если комната СЕЙЧАС в каком-то
  // пространстве И у меня есть право снять child хотя бы в одном из них.
  // Иначе кнопка «Дом» обещала бы действие, которое сервер отклонит.
  const canUnlink = currentSpaces.some((sId) => mayManageSpaceChild(client, sId));

  // Начальное значение: первое доступное пространство, а если их нет,
  // но можно вынуть в «Дом» — пустая строка (она кодирует «Дом»).
  // Без этого при targets.length === 0 кнопка была бы заблокирована,
  // хотя единственное осмысленное действие (вынуть в «Дом») доступно.
  const [targetId, setTargetId] = useState<string | null>(
    targets[0]?.id ?? (canUnlink ? "" : null),
  );
  const { busy, error, run } = useAsyncAction(onClose);

  // Комната может состоять в НЕСКОЛЬКИХ пространствах сразу (Matrix это
  // позволяет). Переносим из первого: множественное членство — редкость,
  // и показывать таблицу «из какого именно» ради неё — перегруз диалога.
  const fromId = currentSpaces[0] ?? null;

  const submit = () =>
    void run(async () => {
      try {
        // targetId === "" кодирует «Дом» (вынуть из пространств).
        await moveRoomToSpace(client, roomId, fromId, targetId === "" ? null : targetId);
      } catch (e) {
        // Ошибку прав показываем ТЕКСТОМ СЕРВЕРА (требование постановки),
        // humanError переведёт M_FORBIDDEN в «Недостаточно прав: …».
        throw new Error(humanError(e));
      }
    });

  const nothingToDo = targets.length === 0 && !canUnlink;

  return (
    <Dialog
      title={tfmt("dialog.moveRoomTitle", { room: roomName })}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy || nothingToDo || targetId === null}
            onClick={submit}
            testId="dialog-submit"
          >
            {busy ? t("dialog.moveRoomProgress") : t("ui.perenesti")}
          </DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.small, color: color.textDim }}>
        {fromId
          ? tfmt("dialog.moveRoomCurrentSpace", {
              space: client.getRoom(fromId)?.name ?? fromId,
            })
          : t("ui.seychas_komnata_vne_prostranstv_dom")}
      </div>
      {nothingToDo ? (
        <div style={{ fontSize: font.small }} data-testid="move-room-empty">
          {/* Объяснение пустоты СЛОВАМИ: иначе неотличимо от поломки. */}
          {t("dialog.noSpacesToMove")}
        </div>
      ) : (
        <label style={{ display: "block", fontSize: font.small, color: color.textDim }}>{t("dialog.moveRoomLabel")}<select
            style={{ ...s.dialogInput, marginTop: 4 }}
            value={targetId ?? ""}
            data-testid="field-move-target"
            onChange={(e) => setTargetId(e.target.value)}
          >
            {canUnlink && <option value="">{t("dialog.outsideSpacesHome")}</option>}
            {targets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      )}
    </Dialog>
  );
}

/**
 * Пригласить человека В ПРОСТРАНСТВО. Пространство — это комната, поэтому
 * переиспользуем inviteUser без какой-либо обёртки (требование постановки).
 *
 * Подсказки: все, кого я знаю по своим пространствам, МИНУС уже состоящие
 * в этом спейсе — spaceUserSuggestions(client, null, spaceId) даёт ровно это
 * (roomId=null собирает по всем моим пространствам, excludeRoomId=spaceId
 * вычитает текущих членов спейса).
 */
function InviteSpaceDialog({
  client,
  spaceId,
  spaceName,
  onClose,
}: {
  client: MatrixClient;
  spaceId: string;
  spaceName: string;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const [userId, setUserId] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const { busy, error, run } = useAsyncAction(() => {});
  const problem = mxidProblem(userId, t);
  const valid = userId.trim().length > 1 && problem === null;

  const suggestions = spaceUserSuggestions(client, null, spaceId);
  const filtered = showSuggest ? filterSuggestions(suggestions, userId) : [];
  const searching = !userId.trim().startsWith("@") && filtered.length > 0;
  const shownProblem = searching ? null : problem;

  const submit = () =>
    void run(async () => {
      const id = userId.trim();
      // Существование — ДО приглашения: иначе человек узнает об опечатке
      // из невнятной серверной ошибки (та же причина, что в InviteDialog).
      const unusable = await whyUserUnusable(client, id);
      if (unusable) throw new Error(unusable);
      try {
        await inviteUser(client, spaceId, id);
      } catch (e) {
        throw new Error(humanError(e));
      }
      setDone(id);
      setUserId("");
      setShowSuggest(false);
    });

  return (
    <Dialog
      title={tfmt("dialog.inviteSpaceTitle", { space: spaceName })}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">{t("common.close")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy || !valid}
            onClick={submit}
            testId="dialog-submit"
          >
            {busy ? t("dialog.inviteProgress") : t("common.invite")}
          </DialogButton>
        </>
      }
    >
      <div
        style={{ position: "relative" }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setShowSuggest(false);
          }
        }}
      >
        <Field
          label="Matrix ID"
          value={userId}
          onChange={(v) => {
            setUserId(v);
            setShowSuggest(true);
          }}
          placeholder="@ivanov:algsuccess.com"
          testId="field-invite-space-user"
          problem={shownProblem}
          onEnter={() => valid && submit()}
        />
        {filtered.length > 0 && (
          <UserSuggest
            suggestions={filtered}
            query={userId}
            onSelect={(id) => {
              setUserId(id);
              setShowSuggest(false);
            }}
            onDismiss={() => setShowSuggest(false)}
          />
        )}
      </div>
      {showSuggest && filtered.length === 0 && (
        <div
          style={{ color: color.textDim, fontSize: font.micro }}
          data-testid="invite-space-suggest-empty"
        >
          {suggestions.length === 0
            ? t("ui.podskazyvat_nekogo_vse_kogo_vy_znaete_p")
            : t("dialog.inviteSuggestEmpty")}
        </div>
      )}
      <div style={{ color: color.textDim, fontSize: font.micro }}>
        {/* Что даёт приглашение — по модели доступа п.4, явно, чтобы не
            было иллюзии «добавил в спейс = он увидел все комнаты». */}
        {t("dialog.inviteSpaceDescription")}</div>
      {done && (
        <div style={{ color: color.ok, fontSize: font.small }} data-testid="invite-space-done">
          {tfmt("dialog.inviteDone", { user: done })}
        </div>
      )}
    </Dialog>
  );
}

/**
 * Переименовать пространство. Пространство — комната, поэтому
 * переиспользуем setRoomName как есть (требование постановки). Диалог
 * отдельный от настроек комнаты, потому что у спейса НЕТ своего экрана
 * настроек — единственная точка входа сейчас это меню рельсы.
 */
function EditSpaceDialog({
  client,
  spaceId,
  onClose,
  onLeft,
}: {
  client: MatrixClient;
  spaceId: string;
  onClose: () => void;
  /** После выхода надо уйти из пространства в ленте, иначе останется пустота. */
  onLeft: (roomId: string) => void;
}) {
  const t = useT();
  const space = client.getRoom(spaceId);
  if (!space) {
    return (
      <Dialog title={t("dialog.spaceSettingsTitle")} onClose={onClose} footer={null}>{t("space.membersNotFound")}</Dialog>
    );
  }
  // ПЕРЕИСПОЛЬЗУЕМ НАСТРОЙКИ КОМНАТЫ (Антон 12.09 11:51: «должно быть
  // edit space, leave space» — не «переименовать», а полноценное
  // редактирование). Пространство в Matrix — та же комната, и у нас уже
  // есть готовый экран с названием, описанием, аватаром и правами
  // доступа. Писать отдельную урезанную форму «только имя» означало бы
  // сознательно сделать пространство беднее комнаты.
  //
  // ПРИГЛАШЕНИЕ И УЧАСТНИКИ — ТЕПЕРЬ ОТСЮДА (Антон 12.09 12:55:
  // «тут должны остаться настройки и выйти» + «остальное уходит в
  // настройки»). Диалоги НЕ переписаны — переехала только точка входа.
  return (
    <SpaceSettingsWithMembers
      client={client}
      spaceRoom={space}
      onClose={onClose}
      onLeft={onLeft}
    />
  );
}

/**
 * Настройки пространства + две строки-перехода, которые раньше жили
 * в меню рельсы. Поддиалог показывается ВМЕСТО настроек, а не поверх:
 * две модалки друг на друге дают два крестика и два разных Escape.
 * Закрытие поддиалога возвращает в настройки, а не закрывает всё.
 */
function SpaceSettingsWithMembers({
  client,
  spaceRoom,
  onClose,
  onLeft,
}: {
  client: MatrixClient;
  spaceRoom: Room;
  onClose: () => void;
  onLeft: (roomId: string) => void;
}) {
  const t = useT();
  const [sub, setSub] = useState<null | "invite" | "members" | "leave">(null);
  const spaceName = spaceRoom.name || spaceRoom.roomId;

  if (sub === "invite") {
    return (
      <InviteSpaceDialog
        client={client}
        spaceId={spaceRoom.roomId}
        spaceName={spaceName}
        onClose={() => setSub(null)}
      />
    );
  }
  if (sub === "members") {
    return (
      <SpaceMembers client={client} spaceId={spaceRoom.roomId} onClose={() => setSub(null)} />
    );
  }
  if (sub === "leave") {
    // Переиспользуем тот же LeaveDialog, что и из меню: в нём уже живёт
    // запрет последнему админу с объяснением причины. Вторая копия
    // проверки разошлась бы с первой при первой же правке.
    return (
      <LeaveDialog
        client={client}
        roomId={spaceRoom.roomId}
        roomName={spaceName}
        onClose={() => setSub(null)}
        onLeft={(id) => {
          // Вышли — настройки больше не имеют смысла, закрываем всё,
          // а не возвращаемся в экран покинутого пространства.
          onLeft(id);
          onClose();
        }}
      />
    );
  }

  // Права на приглашение считаем здесь же: нет прав — нет строки.
  // Список участников виден всем (удаление внутри уже по правам).
  const canInvite = mayInviteToSpace(client, spaceRoom.roomId);

  const row = (label: string, testId: string, onClick: () => void) => (
    <button
      type="button"
      style={{
        ...s.actionButton,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
      }}
      data-testid={testId}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <RoomSettings
      client={client}
      room={spaceRoom}
      onClose={onClose}
      extraActions={
        <div
          style={{
            borderTop: `1px solid ${color.border}`,
            paddingTop: space.md,
            display: "flex",
            flexDirection: "column",
            gap: space.sm,
          }}
        >
          {canInvite &&
            row(t("dialog.spaceInviteAction"), "space-settings-invite", () => setSub("invite"))}
          {row(t("dialog.spaceMembersAction"), "space-settings-members", () => setSub("members"))}
          {/* ВЫЙТИ — ТОЖЕ ЗДЕСЬ (Антон 12.09 12:56 «и выйти надо в
              настройки»). Последней строкой и красным: разрушительное
              действие не должно стоять рядом с обычными переходами. */}
          <button
            type="button"
            style={{
              ...s.actionButton,
              width: "100%",
              textAlign: "left",
              cursor: "pointer",
              color: color.danger,
            }}
            data-testid="space-settings-leave"
            onClick={() => setSub("leave")}
          >{t("dialog.spaceLeaveAction")}</button>
        </div>
      }
    />
  );
}

